<template>
    <div>
        <div v-if="is_loading" class="text-muted py-3">
            <i class="fa fa-spinner fa-spin me-1"></i> Memuat data obat...
        </div>

        <!-- Lembar resep elektronik -->
        <template v-else-if="jenis === 'elektronik'">
            <div v-if="resep.length">
                <div class="row g-3 small mb-3 border-bottom pb-3">
                    <div v-for="item in identitas" :key="item.label" class="col-md-6 col-lg-4">
                        <div class="text-muted">{{ item.label }}</div>
                        <div class="fw-semibold">{{ item.value || "-" }}</div>
                    </div>
                </div>

                <div v-for="r in resep" :key="r.id" class="mb-4">
                    <div class="d-flex flex-wrap justify-content-between gap-2 mb-2">
                        <span class="badge bg-success">Resep {{ r.id }}</span>
                        <span class="text-muted small">
                            {{ r.dokter || "-" }} — {{ formatTanggalJam(r.jam || r.tanggal) }}
                        </span>
                    </div>

                    <div class="border rounded p-3 bg-white">
                        <div v-for="obat in r.non_racikan" :key="obat.kode_obat" class="resep-baris">
                            <div class="resep-r">R/</div>
                            <div>
                                <div class="fw-semibold">{{ obat.nama_obat }}</div>
                                <div class="text-muted small">
                                    S {{ obat.signa_manual || obat.signa }}
                                    <template v-if="obat.jumlah_hari">
                                        — {{ obat.jumlah_hari }} hari
                                    </template>
                                </div>
                            </div>
                            <div class="text-nowrap">No. {{ obat.qty }}</div>
                        </div>

                        <div v-for="rd in r.racikan_dokter" :key="`rd-${rd.no_racikan}`" class="resep-baris">
                            <div class="resep-r">R/</div>
                            <div>
                                <div class="fw-semibold">Racikan ke-{{ rd.no_racikan }}</div>
                                <div class="text-muted small">S {{ rd.signa1 }} dd {{ rd.signa2 }}</div>
                                <ul class="mb-0 ps-3 small">
                                    <li v-for="obat in rd.obat" :key="obat.kode_obat">
                                        {{ obat.nama_obat }}
                                    </li>
                                </ul>
                            </div>
                            <div class="text-nowrap">No. {{ rd.qty }}</div>
                        </div>

                        <div v-for="(rc, index) in r.racikan" :key="`rc-${index}`" class="resep-baris">
                            <div class="resep-r">R/</div>
                            <div>
                                <div class="fw-semibold">{{ rc.Racikan }}</div>
                                <div class="text-muted small">S {{ rc.Signa || "-" }}</div>
                            </div>
                            <div class="text-nowrap">No. {{ rc.Catatan }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <p v-else class="text-muted mb-0">
                <i class="fa fa-inbox me-1"></i> Belum ada resep elektronik pada registrasi ini.
            </p>
        </template>

        <!-- Daftar resep / apotik / transaksi obat -->
        <template v-else-if="resep.length">
            <div v-for="r in resep" :key="r.id" class="card border mb-3">
                <div class="card-header bg-light d-flex flex-wrap align-items-center gap-2 py-2">
                    <span class="badge bg-success">Resep ke-{{ r.resep_ke }}</span>
                    <span class="fw-semibold small">{{ waktuResep(r) }}</span>
                    <span v-if="r.poli" class="text-muted small">{{ r.poli }}</span>
                    <span class="text-muted small ms-auto">
                        {{ r.dokter || "-" }}
                        <template v-if="r.regno">— No. Reg {{ r.regno }}</template>
                    </span>
                </div>

                <div class="table-responsive">
                    <table class="table table-sm align-middle mb-0">
                        <thead class="table-light">
                            <tr>
                                <th style="width: 110px">Kode</th>
                                <th>Nama Obat</th>
                                <th style="width: 80px">Qty</th>
                                <th style="width: 170px">Signa</th>
                                <th>Keterangan</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="obat in r.obat" :key="`${r.id}-${obat.kode_obat}-${obat.no_racikan}`">
                                <td class="text-muted">{{ obat.kode_obat }}</td>
                                <td>
                                    {{ obat.nama_obat }}
                                    <span v-if="obat.jenis === 4" class="badge bg-secondary ms-1">
                                        Racikan ke-{{ obat.no_racikan }}
                                    </span>
                                </td>
                                <td>{{ obat.qty }} {{ obat.satuan }}</td>
                                <td>{{ obat.signa_manual || obat.signa }}</td>
                                <td class="small text-muted">
                                    {{ obat.keterangan_pakai || obat.cara_minum || "" }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="(r.racikan || []).length" class="table-responsive border-top">
                    <table class="table table-sm align-middle mb-0">
                        <thead class="table-light">
                            <tr>
                                <th>Racikan</th>
                                <th style="width: 170px">Signa</th>
                                <th style="width: 80px">Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(rc, index) in r.racikan" :key="`${r.id}-rc-${index}`">
                                <td>{{ rc.Racikan }}</td>
                                <td>{{ rc.Signa }}</td>
                                <td>{{ rc.Catatan }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <p v-else class="text-muted mb-0">
            <i class="fa fa-inbox me-1"></i> Tidak ada {{ label }} untuk pasien ini.
        </p>
    </div>
</template>

<script>
const ENDPOINT = {
    resep: ["/api/riwayat-pasien/obat/resep", "riwayat resep dokter"],
    apotik: ["/api/riwayat-pasien/obat/apotik", "riwayat apotik"],
    transaksi: ["/api/riwayat-pasien/obat/transaksi", "riwayat transaksi obat"],
    elektronik: ["/api/riwayat-pasien/obat/resep-elektronik", "resep elektronik"],
};

export default {
    name: "RiwayatObatPasien",

    props: {
        medrec: {
            type: [String, Number],
            default: null,
        },
        regno: {
            type: [String, Number],
            default: null,
        },
        /** "resep", "apotik", "transaksi", atau "elektronik". */
        jenis: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            resep: [],
            head: {},
            is_loading: false,
        };
    },

    computed: {
        label() {
            return ENDPOINT[this.jenis][1];
        },

        identitas() {
            const head = this.head || {};

            return [
                { label: "Nama Pasien", value: head.Firstname },
                { label: "No. RM", value: head.Medrec },
                { label: "No. Registrasi", value: head.Regno },
                { label: "Tanggal Lahir / Usia", value: [this.formatTanggal(head.Bod), head.usia].filter(Boolean).join(" / ") },
                { label: "Poli", value: head.NMPoli },
                { label: "DPJP", value: head.NmDoc },
                { label: "No. SEP", value: head.NoSep },
                { label: "Alamat", value: head.Address },
            ];
        },
    },

    mounted() {
        this.getRiwayat();
    },

    methods: {
        getRiwayat() {
            this.is_loading = true;

            this.axios
                .get(ENDPOINT[this.jenis][0], {
                    params: { medrec: this.medrec, regno: this.regno },
                })
                .then(({ data }) => {
                    const payload = data.data || {};

                    this.resep = Array.isArray(payload) ? payload : payload.resep || [];
                    this.head = payload.head || {};
                })
                .catch(() => {
                    this.resep = [];
                    this.head = {};
                })
                .finally(() => (this.is_loading = false));
        },

        formatTanggal(value) {
            if (!value) return "";
            return moment(String(value)).format("DD-MM-YYYY");
        },

        formatTanggalJam(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD-MM-YYYY HH:mm");
        },

        waktuResep(r) {
            const tanggal = this.formatTanggal(r.tanggal);
            const jam = r.jam ? moment(String(r.jam)).format("HH:mm") : "";

            return [tanggal, jam].filter(Boolean).join(" ") || "-";
        },
    },
};
</script>

<style scoped>
.resep-baris {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px dashed #dee2e6;
}

.resep-baris:last-child {
    border-bottom: 0;
}

.resep-baris > div:nth-child(2) {
    flex: 1;
}

.resep-r {
    width: 32px;
    font-style: italic;
    font-size: 1.25rem;
    font-weight: 700;
}
</style>
